import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons/faCircleCheck';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { TypedMemo } from '../../common/types';
import { TrackerDetection, TrackerDetectionKind, unsupportedTrackerNames } from './detectTracker';
import { trackerProviderMark } from './trackerProviders';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './SMLTrackers.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SnippetStatusBarProps {
  detection: TrackerDetection;
}

/** The bold chunk the messages carry, hoisted so it is one component type. */
const strong = (chunks: React.ReactNode[]) => <strong>{chunks}</strong>;

/**
 * What the paste box made of the snippet, said out loud under it.
 *
 * The old modal took the paste in silence, so an artist learned that a tag
 * never fired only from an empty report, weeks later.
 */
const SnippetStatusBarComponent: React.FC<SnippetStatusBarProps> = ({ detection }) => {
  const intl = useIntl();

  if (detection.kind === TrackerDetectionKind.empty) {
    return (
      <div className={styles.statusBar}>
        {intl.formatMessage({ id: 'app.sml.trackers.status.empty', defaultMessage: 'Nothing pasted yet.' })}
      </div>
    );
  }

  if (detection.kind === TrackerDetectionKind.recognised) {
    const mark = trackerProviderMark(detection.type);
    return (
      <div className={clsx(styles.statusBar, styles.statusGood)}>
        <FontAwesomeIcon icon={faCircleCheck} />
        <span>
          {intl.formatMessage(
            {
              id: 'app.sml.trackers.status.recognised',
              defaultMessage: '<b>{provider}</b> · ID {trackerId}',
            },
            {
              b: strong,
              provider: mark?.name ?? '',
              trackerId: <span className={styles.statusId}>{detection.trackerId}</span>,
            }
          )}
        </span>
      </div>
    );
  }

  return (
    <div className={clsx(styles.statusBar, styles.statusBad)}>
      <FontAwesomeIcon icon={faTriangleExclamation} />
      <span>
        {detection.kind === TrackerDetectionKind.unsupported
          ? intl.formatMessage(
              {
                id: 'app.sml.trackers.status.unsupported',
                defaultMessage: "This looks like a <b>{tracker}</b>, which we don't support yet.",
              },
              { b: strong, tracker: unsupportedTrackerNames[detection.tracker] }
            )
          : intl.formatMessage({
              id: 'app.sml.trackers.status.unreadable',
              defaultMessage: "Couldn't find a tracking ID in this snippet.",
            })}
      </span>
    </div>
  );
};

export const SnippetStatusBar = TypedMemo(SnippetStatusBarComponent);
